import { computed, inject, Injectable, signal } from '@angular/core';
import { SessionService } from './session.service';
import { ToastService } from './toast.service';
import { Router } from '@angular/router';
// import { FirestoreService } from './firestore.service';
// import { doc, Firestore, limit, orderBy, setDoc, updateDoc, where, writeBatch } from '@angular/fire/firestore';
// import { Auth, authState } from '@angular/fire/auth';
import { DbTable } from '@app/models/models.helpers';
import { SupabaseService } from './supabase.service';
import { RealtimeChannel } from '@supabase/supabase-js';

@Injectable({
  providedIn: 'root'
})
export class InboxService {
  sessionService = inject(SessionService);
  toastService = inject(ToastService);
  supabase = inject(SupabaseService);
  private _inbox = signal<DbTable<'inbox_items'>[]>([]);
  inbox = computed(() => this._inbox());
  numUnread = computed(() => this.inbox().filter(item => item.status === 'UNREAD').length);
  router = inject(Router);

  inboxSubscription: RealtimeChannel | null = null;

  constructor() {
    this.setupRealtimeSubscription();
  }

  async loadInbox() {
    const { data, error } = await this.supabase.client.from('inbox_items')
    .select('*')
    .filter('status', 'neq', 'ARCHIVED')
    .eq('user_id', this.sessionService.user()?.id);
    if (error) {
      console.error('Error loading inbox:', error);
      return;
    }
    this.updateInbox(data);
  }

  cleanupSubscription() {
    if (this.inboxSubscription) {
      this.inboxSubscription.unsubscribe();
      this.inboxSubscription = null;
    }
  }

  setupRealtimeSubscription(): Promise<void> {
    this.cleanupSubscription();
    return new Promise((resolve, reject) => {
      this.inboxSubscription = this.supabase.client
        .channel('inbox_items')
        .on(
          'postgres_changes', 
          {
            event: 'INSERT',
            schema: 'public',
            table: 'inbox_items',
            filter: `user_id=eq.${this.sessionService.user()?.id}`,
          },
          (payload) => {
            console.log('Realtime inbox item update:', payload);
            this.updateInbox([...this.inbox(), payload.new as DbTable<'inbox_items'>]);
          },
        )
        .on(
          'postgres_changes',
          {
            event: 'UPDATE',
            schema: 'public',
            table: 'inbox_items',
            filter: `user_id=eq.${this.sessionService.user()?.id}`,
          },
          (payload) => {
            console.log('Realtime inbox item update:', payload);
            this.updateInbox(this.inbox().map(item => item.id === payload.new['id'] ? payload.new as DbTable<'inbox_items'> : item));
          },
        )
        .on(
          'postgres_changes',
          {
            event: 'DELETE',
            schema: 'public',
            table: 'inbox_items',
            filter: `user_id=eq.${this.sessionService.user()?.id}`,
          },
          (payload) => {
            console.log('Realtime inbox item delete:', payload);
            this.updateInbox(this.inbox().filter(item => item.id !== payload.old['id']));
          },
        )
        .subscribe((status, err) => {
          if (status === 'SUBSCRIBED') {
            console.log('Realtime subscription active for inbox items');
            resolve();
          }
          if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT') {
            console.error('Realtime subscription failed:', err);
            reject(err);
          }
        });
    });
  }

  updateInbox(docs: DbTable<'inbox_items'>[], isFirstLoad = false) {
    // loop over the existing inbox items, add new ones, update existing ones
    const existingItems = this.inbox();
    const newItems = docs.filter(doc => !existingItems.some(item => item.id === doc.id));
    const updatedItems = docs.filter(doc => existingItems.some(item => item.id === doc.id));
    this._inbox.set([...newItems, ...updatedItems]);
    if (!isFirstLoad) {
      for (const item of newItems) {
        if (item.show_toast) {
          this.toastService.add({
            key: 'tr',
            id: item.id,
            severity: item.severity || 'info',
            summary: item.title,
            detail: item.description,
            data: item,
            life: 10000,
            closable: true,
          });
        }
      }
    }
  }

  async markAsRead(item: DbTable<'inbox_items'>) {
    // update the inbox item in the local state
    // this._inbox.update(items => items.map(i => i.id === item.id ? { ...i, status: 'READ', show_toast: false } as DbTable<'inbox_items'> : i));
    // update the inbox item in the database
    await this.supabase.client.from('inbox_items').update({ status: 'READ', show_toast: false }).eq('id', item.id);
  }

  async markAsReadById(id: string) {
    await this.supabase.client.from('inbox_items').update({ status: 'READ', show_toast: false }).eq('id', id);
  }

  async markAllAsRead() {
    // update the inbox items in the local state
    const items = this.inbox().filter(item => item.status === 'UNREAD');
    await this.supabase.client.from('inbox_items').update({ status: 'READ', show_toast: false }).in('id', items.map(item => item.id));
  }

  async markAsToasted(id: string) {
    // update the inbox item in the local state
    // this._inbox.update(items => items.map(i => i.id === id ? { ...i, show_toast: false } as InboxItem : i));
    // update the inbox item in the database
    await this.supabase.client.from('inbox_items').update({ show_toast: false }).eq('id', id);
  }

  isActionable(item: Partial<DbTable<'inbox_items'>> | null | undefined): boolean {
    if (!item) return false;
    return !!item.action_path || !!item.action_key;
  }

  async handleClickAction(
    item: Partial<DbTable<'inbox_items'>> | null | undefined,
    options: { markAsRead?: boolean; markAsToasted?: boolean } = {}
  ): Promise<boolean> {
    console.log('handleClickAction', item);
    if (!item) return false;
    const shouldMarkAsRead = options.markAsRead ?? true;
    const shouldMarkAsToasted = options.markAsToasted ?? false;
    let didNavigate = false;

    try {
      if (item.action_path) {
        didNavigate = await this.router.navigateByUrl(item.action_path);
      } else if (item.action_key) {
        switch (item.action_key) {
          case 'VIEW_ORG_INVITATION': {
            console.log('VIEW_ORG_INVITATION', item);
            const meta = item.meta as { organization_id?: string; invitation_id?: string } | null;
            const invitation = await this.supabase.client.from('organization_invitations').select('*').eq('id', meta?.invitation_id).single();
            if (invitation.data) {
              const token = invitation.data.token;
              didNavigate = await this.router.navigate(['/invite'], {
                queryParams: {
                  token,
                  returnUrl: this.router.url,
                },
              });
            }
            break;
          }
          default:
            console.error('Unknown inbox action key', item.action_key);
            break;
        }
      }
    } catch (error) {
      console.error('Error handling inbox action', error);
    } finally {
      if (item.id) {
        if (shouldMarkAsRead) {
          await this.markAsReadById(item.id);
        }
        if (shouldMarkAsToasted) {
          await this.markAsToasted(item.id);
        }
      }
    }

    return didNavigate;
  }

  async archiveMessage(message: DbTable<'inbox_items'>) {
    // update the inbox item in the local state
    // this._inbox.update(items => items.filter(i => i.id !== message.id));
    // update the inbox item in the database
    await this.supabase.client.from('inbox_items').update({ status: 'ARCHIVED', show_toast: false }).eq('id', message.id);
  }

  async deleteMessage(message: DbTable<'inbox_items'>) {
    // delete the inbox item in the database
    await this.supabase.client.from('inbox_items').delete().eq('id', message.id);
  }

  async markAllAsArchived() {
    // update the inbox items in the local state
    // this._inbox.update(items => items.map(item => item.status === 'UNREAD' ? { ...item, status: 'ARCHIVED', show_toast: false } as DbTable<'inbox_items'> : item));
    // update the inbox items in the database
    await this.supabase.client.from('inbox_items').update({ status: 'ARCHIVED', show_toast: false }).in('id', this.inbox().map(item => item.id));
  }
}
